import React, { ReactNode, forwardRef } from "react";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  size?: "xs" | "sm" | "md" | "lg" | "icon";
  variant?: "default" | "primary" | "outline" | "ghost" | "destructive" | "danger" | "success" | "warning" | "error" | "info" | "secondary";
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  loading?: boolean;
  className?: string;
  title?: string;
  fullWidth?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      size = "md",
      variant = "default",
      type = "button",
      startIcon,
      endIcon,
      onClick,
      className = "",
      disabled = false,
      loading = false,
      title,
      fullWidth = false,
      ...props
    },
    ref
  ) => {
    // Size Classes
    const sizeClasses = {
      xs: "px-3 py-1.5 text-xs",
      sm: "px-4 py-2 text-sm",
      md: "px-5 py-3 text-sm",
      lg: "px-6 py-3.5 text-sm",
      icon: "p-2",
    };

    // Variant Classes
    const variantClasses = {
      // Radix UI variants
      default: "bg-gray-900 text-white shadow-theme-xs hover:bg-gray-800 dark:bg-gray-50 dark:text-gray-900 dark:hover:bg-gray-200",
      secondary: "bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700",
      destructive: "bg-red-600 text-white hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800",
      
      // Danger variant (same as destructive for consistency)
      danger: "bg-red-600 text-white hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800",
      
      // Legacy variants (backward compatibility)
      primary: "bg-brand-500 text-white shadow-theme-xs hover:bg-brand-600 disabled:bg-brand-300",
      outline: "bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-700 dark:hover:bg-white/[0.03] dark:hover:text-gray-300",
      ghost: "bg-transparent text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800",
      success: "bg-green-500 text-white hover:bg-green-600 disabled:bg-green-300",
      warning: "bg-yellow-500 text-white hover:bg-yellow-600 disabled:bg-yellow-300",
      error: "bg-red-500 text-white hover:bg-red-600 disabled:bg-red-300",
      info: "bg-blue-500 text-white hover:bg-blue-600 disabled:bg-blue-300",
    };

    // Loading spinner component
    const LoadingSpinner = () => (
      <svg
        className="animate-spin h-4 w-4 text-current"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        ></circle>
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
    );

    return (
      <button
        ref={ref}
        type={type}
        className={`inline-flex items-center justify-center font-medium gap-2 rounded-lg transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-70 ${
          fullWidth ? "w-full" : ""
        } ${className} ${
          sizeClasses[size]
        } ${variantClasses[variant]} ${
          disabled || loading ? "cursor-not-allowed" : "cursor-pointer"
        }`}
        onClick={onClick}
        disabled={disabled || loading}
        title={title}
        {...props}
      >
        {loading && <LoadingSpinner />}
        {!loading && startIcon && <span className="flex items-center">{startIcon}</span>}
        {children}
        {!loading && endIcon && <span className="flex items-center">{endIcon}</span>}
      </button>
    );
  }
);

Button.displayName = "Button";

export default Button;